import React, { useState } from 'react';
import './Login.css';
import api from '../services/api';

const getPathUserType = () => {
  if (typeof window === 'undefined') return 'school';
  const path = window.location.pathname || '';
  if (path.includes('/parent')) return 'parent';
  if (path.includes('/admin')) return 'admin';
  if (path.includes('/sub-agency')) return 'sub_agency';
  if (path.includes('/agency')) return 'agency';
  if (path.includes('/teacher')) return 'teacher';
  return 'school';
};

const getLoginContext = () => {
  if (typeof window === 'undefined') {
    return { title: 'School Login', subtitle: 'Welcome back! Please sign in to your account' };
  }

  const path = window.location.pathname || '';

  if (path.includes('/parent')) {
    return {
      title: 'Parent Login',
      subtitle: 'Parents portal - sign in to track your child'
    };
  }

  if (path.includes('/admin')) {
    return {
      title: 'Admin Login',
      subtitle: 'Administrative access - secure area'
    };
  }

  if (path.includes('/sub-agency')) {
    return {
      title: 'Sub-agency Login',
      subtitle: 'Sub-agency portal - manage your schools'
    };
  }

  if (path.includes('/agency')) {
    return {
      title: 'Agency Login',
      subtitle: 'Agency portal - manage multiple schools'
    };
  }

  if (path.includes('/teacher')) {
    return {
      title: 'Class Teacher Login',
      subtitle: 'Assign devices and check student device status'
    };
  }

  return {
    title: 'School Login',
    subtitle: 'School staff portal - manage routes and attendance'
  };
};

const Login = () => {
  const userType = getPathUserType();
  const supportsForgotPassword = userType === 'school' || userType === 'agency' || userType === 'sub_agency';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userType: 'school'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState('login'); // login | forgot | otp | reset
  const [resetEmail, setResetEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const resetForgotState = () => {
    setMode('login');
    setError('');
    setSuccess('');
    setOtp('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.login(userType, formData.email, formData.password);

      api.setAuthToken(response.token);

      const userData = {
        ...response.user,
        user_type: response.user.role
      };

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('userLogin', { detail: userData }));
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const email = (resetEmail || formData.email || '').trim();
      if (!email) {
        throw new Error('Email is required');
      }
      await api.forgotPassword(userType, email);
      setResetEmail(email);
      setSuccess('If an account exists for this email, an OTP has been sent. Check your inbox.');
      setMode('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await api.verifyResetOtp(userType, resetEmail, otp);
      setResetToken(data.reset_token);
      setSuccess('OTP verified. Enter your new password.');
      setMode('reset');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      if (newPassword.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }
      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }
      await api.resetPassword(resetToken, newPassword);
      setSuccess('Password reset successfully. You can now sign in.');
      setFormData((prev) => ({ ...prev, email: resetEmail, password: '' }));
      setMode('login');
      setOtp('');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const { title: loginTitle, subtitle: loginSubtitle } = getLoginContext();

  const renderForgotFlow = () => {
    if (mode === 'forgot') {
      return (
        <form onSubmit={handleSendOtp} className="login-form">
          <div className="form-group">
            <label>Registered Email</label>
            <div className="input-wrapper">
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your account email"
                required
              />
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Sending OTP...' : 'Send OTP'}
          </button>
          <button type="button" className="link-btn" onClick={resetForgotState}>
            ← Back to Sign In
          </button>
        </form>
      );
    }

    if (mode === 'otp') {
      return (
        <form onSubmit={handleVerifyOtp} className="login-form">
          <p className="helper-text">OTP sent to <strong>{resetEmail}</strong></p>
          <div className="form-group">
            <label>Enter OTP</label>
            <div className="input-wrapper">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit OTP"
                required
              />
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          <button type="submit" className="login-btn" disabled={loading || otp.length !== 6}>
            {loading ? 'Verifying...' : 'Verify OTP'}
          </button>
          <button type="button" className="link-btn" onClick={handleSendOtp} disabled={loading}>
            Resend OTP
          </button>
          <button type="button" className="link-btn" onClick={resetForgotState}>
            ← Back to Sign In
          </button>
        </form>
      );
    }

    return (
      <form onSubmit={handleResetPassword} className="login-form">
        <div className="form-group">
          <label>New Password</label>
          <div className="input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              minLength={6}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
        </div>
        <div className="form-group">
          <label>Confirm Password</label>
          <div className="input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={6}
            />
          </div>
        </div>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <button type="submit" className="login-btn" disabled={loading}>
          {loading ? 'Saving...' : 'Reset Password'}
        </button>
        <button type="button" className="link-btn" onClick={resetForgotState}>
          ← Back to Sign In
        </button>
      </form>
    );
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo">
            <img src="/logo.png" alt="EduManage logo" className="logo-img" />
          </div>
          <h1 className="logo-title">
            {mode === 'login' ? loginTitle : 'Forgot Password'}
          </h1>
          <p className="subtitle">
            {mode === 'login'
              ? loginSubtitle
              : 'Reset your school or agency account password using email OTP'}
          </p>
        </div>

        {mode === 'login' ? (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label>Email Address</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button
              type="submit"
              className="login-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        ) : (
          renderForgotFlow()
        )}

        <div className="login-footer">
          {supportsForgotPassword && mode === 'login' && (
            <button
              type="button"
              className="forgot-password"
              onClick={() => {
                setResetEmail(formData.email || '');
                setError('');
                setSuccess('');
                setMode('forgot');
              }}
            >
              Forgot Password?
            </button>
          )}
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <button
              type="button"
              className="back-btn"
              onClick={() => window.location.href = '/'}
              style={{
                background: 'none',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'underline'
              }}
            >
              ← Back to Login Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
